import { Directive, inject, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core';
import { IProcessProfile } from '@partners/api';
import { CatalogService } from '@partners/catalog';
import { ContextBuilder, ProfileService } from '@partners/kernel';
import { NavigationService } from '@partners/navigation';
import { SettingsMap, TFeature } from '@partners/settings';
import { isNotEmpty, takeOnce } from '@partners/utils';
import { UtmService } from '@partners/utm';
import { Observable, Subscription } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

@Directive({
  selector: '[appInitializer]',
  standalone: true
})
export class AppInitializerDirective implements OnInit, OnDestroy {
  private _appTemplate = inject(TemplateRef);
  private _containerRef = inject(ViewContainerRef);
  private _profileService = inject(ProfileService);
  private _navigationService = inject(NavigationService);
  private _catalogService = inject(CatalogService);
  private _utmService = inject(UtmService);
  private _subscription: Subscription;

  constructor() {
    this._subscription = new Subscription();
  }

  ngOnInit(): void {
    if (this.shouldIgnoreNavigation()) {
      return;
    }
    const { create$, change$ } = this._profileService;

    /** add a listener to a new profile creation event */
    this.addProfileCreatedListener(create$);

    /** add a listener to current profile update event */
    this.addProfileChangedListener(change$);

    /** create the app profile from url query params on first page load */
    this._profileService.createAppProfile$().pipe(takeOnce()).subscribe();

    /** loads teh utm params from url quety on first page load */
    this._utmService.loadUtmParams$().pipe(takeOnce()).subscribe();
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }

  /**
   * @description
   * subscribe to profile "change" event. this event emits in two cases:
   * (1) on browser's refresh event
   * (2) in dev mode, when using our custom debugger layer we can change profile to debug the different
   * profiles & processes (by partner | by product). after resolving the profile from cache (store) we
   * then allow the content template to be loaded
   * @param change$ the profile "create" event stream
   * */
  private addProfileChangedListener(change$: Observable<IProcessProfile>) {
    const changeSub = change$
      .pipe(
        filter((profile: IProcessProfile) => isNotEmpty(profile?.partner)),
        tap((profile) => {
          this.resolveView(profile);
          this.resolveCatalog();
        })
      )
      .subscribe();
    this._subscription.add(changeSub);
  }

  /**
   * @description
   * subscribe to profile "create" event. the event emits once the new profile is created from url
   * params, this happens on the app's initialization (first load). the event output bring us the new
   * profile and we then navigate to the default view as defined in the profile. the content template
   * is suspended until the profile is resolved
   * @param created$ the profile "create" event stream
   * */
  private addProfileCreatedListener(create$: Observable<IProcessProfile>) {
    const createSub = create$
      .pipe(
        filter((profile: IProcessProfile) => isNotEmpty(profile?.partner)),
        tap((profile) => {
          this.resolveCatalog();
          this.resolveView(profile);
          if (profile?.params?.view) {
            const { path } = SettingsMap.get(profile.params.view as TFeature);
            this._navigationService.moveAny(path);
          }
        })
      )
      .subscribe();

    this._subscription.add(createSub);
  }

  /**
   * @description
   * dynamically clear and then add the host's template, in this case - the root of the application
   * this means that until this method is called - no content is rendered to the screen. this method
   * is called when the app's page is loaded, either on first load or br refreshing the browser
   * @param profile
   */
  private resolveView(profile: IProcessProfile) {
    this._containerRef.clear();
    this._containerRef.createEmbeddedView(this._appTemplate);
  }

  /**
   * @description
   * once the app is initialized, and the profile was created, we can immediately launch an async http
   * request to get catalog collections, this operation runs async in the background and does not delay
   * the app view.
   */
  private resolveCatalog() {
    const context = new ContextBuilder().disableLoader().addUrlModifiers({ omitProcess: true }).build();
    this._catalogService.loadAll$({ context });
  }
  /**
   * @description
   * Checks if the current URL has a query parameter 'ignoreNavigation'.
   * If the parameter is present, its value returned.
   * for example: a scan process using scanovate product, makes a redirection after the scan process ends.
   * in this case, the iframe url will have the same origin of partners app (for security reasons), and we do not want our app to create
   * new profile etc.
   * @returns {string | null} The value of the 'ignoreNavigation' query parameter, or null if it doesn't exist.
   */
  private shouldIgnoreNavigation() {
    const uri = new URL(window.location.href);
    return uri?.searchParams?.get('ignoreNavigation');
  }
}
